import { deepMerge, clone } from '@/utils';
import { OPTIONS_TYPE, CHANNEL_INTERFACE_TYPE } from '@/enums';
import { checkboxFilterNone } from '@/tools/form';
import { generateFieldName } from '@/views/fusion/investment/intelligence-create/tools';
import {
  CAMPAIGN_MODULE_KEY,
  CREATIVE_MODULE_KEY,
  CAMPAIGN_BASE_FORM_KEY,
  CAMPAIGN_DELIVERY_FORM_KEY,
  CAMPAIGN_SITE_FORM_KEY,
  CAMPAIGN_BUDGET_AND_BID_FORM_KEY,
  CAMPAIGN_SCHEDULE_FORM_KEY,
  CREATIVE_BASE_FORM_KEY,
  TARGETING_MODULE_KEY,
  MATERIAL_MODULE_KEY,
  TITLE_MODULE_KEY,
  SITE_MODULE_KEY,
  CREATIVE_CONTENT_FORM_KEY,
} from '@/views/fusion/investment/intelligence-create/strategy/constants';
import {
  getCampaignConfig,
  getCreativeConfig,
  getAssignmentConfig,
} from '@/views/fusion/investment/intelligence-create/model/form';
import { createCampaignNameSpecialRule } from '@/views/fusion/investment/intelligence-create/tools/form/validator';
import calculator from '@/utils/calculator';
import { generateHtmlContent } from '@/views/fusion/investment/intelligence-create/tools/form';

/**
 * 获取公共模块字段
 * @returns
 */
export function getPublicFields() {
  return {
    campaign_config: {
      title: '广告信息',
      config: {
        base_form: {
          title: '基础配置',
          config: getCampaignConfig([
            'campaign_name',
            'fusion__campaign_num',
            'is_label',
            'labels',
          ]),
        },
        delivery_form: {
          title: '投放游戏',
          config: getCampaignConfig([
            'fusion__channel_code',
            'delivery_platform_type',
            'delivery_content_id',
          ]),
        },
        site_form: {
          title: '广告版位',
          config: getCampaignConfig([
            'site_set_type',
            'site_sets',
          ]),
        },
        schedule_form: {
          title: '广告排期',
          config: getCampaignConfig([
            'schedule_type',
            'delivery_date',
            'time_period_type',
            'schedule_time',
            'time_period',
          ]),
        },
        budget_and_bid_form: {
          title: '广告预算与出价',
          config: getCampaignConfig([
            'daily_budget_type',
            'daily_budget',
            'price_type',
            'price_strategy',
            'convert_id',
            'fusion__optimize_goal',
            'deep_convert_type',
            'price',
            'conversion_cost',
            'deep_convert_price',
          ]),
        },
      },
      props: {
        col: {
          span: 12,
        },
      },
    },
    creative_config: {
      title: '创意信息',
      config: {
        base_form: {
          title: '基础配置',
          config: getCreativeConfig(['creative_name', 'fusion__creative_num_type', 'fusion__creative_num']),
        },
        content_form: {
          title: '内容配置',
          config: getCreativeConfig(['dispatch_rule']),
        },
        creative_components: {
          title: '创意组件',
          config: {},
        },
      },
      props: {
        col: {
          span: 12,
        },
      },
    },
    targeting_config: {
      title: '广告定向',
      config: {
        targeting_form: {
          title: '',
          config: getAssignmentConfig([
            'name',
            'geo_location',
            'gender',
            'audience',
          ]),
        },
      },
      props: {
        col: {
          span: 5,
        },
      },
      selection_config: {
        unique_key: 'id',
        label_key: 'name',
        list: [],
      },
    },
    material_config: {
      title: '视频/图片素材',
      config: {
        material_form: {
          config: {
            material: {
              props: {
                tempMaterial: null,
              },
              value: {
                images: [],
                videos: [],
                tab: 'video',
              },
            },
          },
        },
      },
      props: {
        col: {
          span: 9,
        },
      },
      selection_config: {
        label_key: 'name',
        list: [],
      },
    },
    title_config: {
      title: '标题',
      /**
       * 标题类型
       * library | component
       * library：标题库
       * component：文案组件
       */
      type: 'library',
      config: {
        base_form: {
          title: '',
          config: {},
        },
      },
      props: {
        col: {
          span: 5,
        },
      },
      selection_config: {
        unique_key: 'id',
        label_key: 'name',
        list: [],
      },
    },
    site_config: {
      title: '落地页',
      config: {},
      props: {
        col: {
          span: 5,
        },
      },
      selection_config: {
        unique_key: 'id',
        advertiser_id_key: 'advertiser_id',
        label_key: 'name',
        list: [],
      },
    },
  };
}

/**
 * 获取模块配置骨架
 * @returns {Object}
 */
export function getModuleSkeleton() {
  const skin = getPublicFields();
  Object.keys(skin).forEach((key) => delete skin[key].config);
  return skin;
}

export default class FieldStrategy {
  /**
   * 获取公共字段
   * @returns {Object}
   */
  getPublicFields() {
    return getPublicFields();
  }

  /**
   * 获取差异化字段
   * 该方法为抽象方法，必须由子类实现
   */
  getDifferentiatedFields() {
    throw new Error('Must be implemented by subclasses.');
  }

  /**
   * 合并公共字段和差异化字段
   * @returns {Object}
   */
  getFields() {
    return deepMerge([this.getPublicFields(), this.getDifferentiatedFields()]);
  }

  /**
   * 设置配置模块的数据源
   * @param {Object} config 模块表单配置
   * @param {Object} controller 控制器
   */
  async setFieldDataSources(config, controller) {
    this.controller = controller;
    const {
      campaign_config: {
        config: {
          delivery_form,
          site_form,
          budget_and_bid_form,
        },
      },
    } = config;
    const {
      delivery_platform_type,
      delivery_content_id,
      media_type,
      campaign_type,
    } = delivery_form.config;
    const {
      site_sets,
    } = site_form.config;
    const {
      price_type,
      price_strategy,
    } = budget_and_bid_form.config;

    const promises_map = [
      /** 投放平台、投放内容 */
      [
        () => {
          return new Promise((resolve, reject) => {
            controller.getDeliveryPlatformEnums().then(res => {
              if (res.length === 0) {
                reject([]);
                return;
              }
              /** 投放平台 */
              delivery_platform_type.options = res;
              /** 投放内容 */
              const params = { delivery_platform_type: delivery_platform_type.value };
              controller.getDeliveryContent(params).then(resolve).catch(reject);
            }).catch(reject);
          });
        },
        (res) => {
          delivery_content_id.options = res;
        },
      ],
      /** 版位信息 */
      [
        () => {
          const { channel_interface } = controller.config.init_ops;
          const strategy = {
            [CHANNEL_INTERFACE_TYPE.OPPO]: () => {
              return {
                oppo_params: {
                  filtering: {
                    delivery_platform_type: delivery_platform_type.value,
                  },
                },
              };
            },
            [CHANNEL_INTERFACE_TYPE.VIVO]: () => {
              return {
                vivo_params: {
                  filtering: {
                    media_type: media_type.value,
                  },
                },
              };
            },
            [CHANNEL_INTERFACE_TYPE.XIAOMI]: () => {
              return {
                xiaomi_params: {
                  filtering: {
                    campaign_type: campaign_type.value,
                  },
                },
              };
            },
            default: () => {},
          };
          return controller.getSiteSet((strategy[channel_interface] || strategy['default'])());
        },
        (res) => {
          site_sets.options = res;
        },
      ],
      /** 出价方式 */
      [
        () => controller.getPriceTypeEnums(),
        (res) => {
          price_type.options = res;
        },
      ],
      /** 出价策略 */
      [
        () => controller.getPriceStrategyEnums(),
        (res) => {
          price_strategy.options = res;
        },
      ],
    ];
    const values = await Promise.all(promises_map.map(([promise]) => promise()));
    promises_map.forEach(([_, handler], index) => handler(values[index]));
  }

  /**
   * 设置定向模块的数据源
   * @param {Object} config 模块表单配置
   * @param {Object} controller 控制器
   * @param {Object} form_fields 源数据表单配置
   */
  async setTargetingDataSources(config, controller, form_fields) {
    const {
      targeting_config: {
        config: {
          targeting_form,
        },
      },
    } = config;
    const {
      geo_location: {
        children: {
          residence_geo_locations,
          current_geo_locations,
        },
      },
      gender,
      audience: {
        children: {
          include_audiences,
          exclude_audiences,
        },
      },
    } = targeting_form.config;

    // 定向字典
    const targeting_dictionary = await controller.getTargetingDictionary(['geo_location', 'gender']);
    const unlimited_selection = {
      label: '不限',
      value: OPTIONS_TYPE.UNLIMITED,
    };

    // 常住地
    residence_geo_locations.options = targeting_dictionary?.geo_location || [];
    // 近期到访
    current_geo_locations.options = targeting_dictionary?.geo_location || [];
    // 性别
    gender.options = [unlimited_selection, ...(targeting_dictionary?.gender || [])];

    // 人群包额外参数
    const strategy = {
      [CHANNEL_INTERFACE_TYPE.VIVO]: () => {
        const { campaign_config: { config: { delivery_form }}} = form_fields;
        const { media_type } = delivery_form.config;

        return {
          vivo_params: {
            filtering: {
              media_type: media_type.value,
            },
          },
        };
      },
      default: () => {},
    };
    const { channel_interface } = controller.config.init_ops;
    // 自定义人群
    const audience_list = await controller.getAudienceList((strategy[channel_interface] || strategy['default'])());
    include_audiences.options = audience_list;
    exclude_audiences.options = audience_list;
  }

  /**
   * 添加事件：固定枚举值的全局公共字段
   * @param {Object} config 模块配置
   */
  addEvent(config) {
    const {
      campaign_config: {
        config: {
          base_form,
          budget_and_bid_form,
          schedule_form,
          site_form,
          delivery_form,
        },
      },
      targeting_config: {
        config: {
          targeting_form,
        },
      },
    } = config;
    const {
      is_label,
      labels,
    } = base_form.config;
    const {
      daily_budget_type,
      daily_budget,
      convert_id,
      fusion__optimize_goal,
      deep_convert_type,
    } = budget_and_bid_form.config;
    const {
      schedule_type,
      delivery_date,
    } = schedule_form.config;
    const { site_set_type, site_sets } = site_form.config;
    const {
      delivery_platform_type,
      delivery_content_id,
      fusion__channel_code: {
        children: {
          fusion__channel_codes,
        },
      },
    } = delivery_form.config;
    const {
      geo_location: {
        children: {
          geo_type,
          location_type,
          residence_geo_locations,
          current_geo_locations,
        },
      },
      audience: {
        children: {
          audience_types,
          include_audiences,
          exclude_audiences,
        },
      },
    } = targeting_form.config;

    /** 素材标签 */
    is_label.on = {
      change: () => {
        const is_custom = is_label.value === 1;
        labels.hidden = !is_custom;
      },
    };

    /**
     * 投放平台
     */
    delivery_platform_type.on = Object.assign(delivery_platform_type.on || {}, {
      change: async() => {
        delivery_content_id.options = await this.controller.getDeliveryContent({
          delivery_platform_type: delivery_platform_type.value,
        });
        const is_ext = delivery_content_id.options.find(opt => {
          return opt.value === delivery_content_id.value;
        });
        delivery_content_id.value = is_ext ? delivery_content_id.value : '';
        delivery_content_id.on.change();
      },
    });

    /**
     * 投放内容
     */
    delivery_content_id.on = Object.assign(delivery_content_id.on || {}, {
      change: () => {
        /** 若投放内容改变，清空已选择的转化事件、优化目标、深度优化目标 */
        if (delivery_content_id.value !== delivery_content_id.backup_value) {
          convert_id.value = '';
          fusion__optimize_goal.value = '';
          deep_convert_type.value = '';
          (fusion__channel_codes.value?.length ? fusion__channel_codes.value : []).forEach(({ list = [] }) => {
            list.forEach(item => {
              delete item.conversion;
            });
          });
        }
        delivery_content_id.backup_value = delivery_content_id.value;
      },
    });

    /**
     * 版位类型
     */
    site_set_type.on = {
      change() {
        /** 是否是自动版位 */
        const is_auto = site_set_type.value === 1;
        site_sets.hidden = is_auto;
      },
    };

    /**
     * 日预算类型
     */
    daily_budget_type.on = {
      change() {
        const is_day = daily_budget_type.value === 'BUDGET_MODE_DAY';
        daily_budget.hidden = !is_day;
      },
    };

    /**
     * 投放日期类型
     */
    schedule_type.on = {
      change() {
        const is_start_end = schedule_type.value === 'SCHEDULE_START_END';
        delivery_date.hidden = !is_start_end;
      },
    };

    const {
      time_period_type,
      schedule_time,
      time_period,
    } = schedule_form.config;

    /**
     * 投放时间类型
     */
    time_period_type.on = {
      change: () => {
        const val = time_period_type.value;
        const is_visible = !time_period_type.hidden;
        const is_day_specific = val === 'TIME_PERIOD_DAY_SPECIFIC';
        const is_hour_specific = val === 'TIME_PERIOD_HOUR_SPECIFIC';
        schedule_time.hidden = !(is_day_specific && is_visible);
        time_period.hidden = !(is_hour_specific && is_visible);
      },
    };

    /**
     * 地域选项
     */
    geo_type.on = {
      change() {
        const is_location = geo_type.value === 'LOCATION';
        location_type.hidden = !is_location;
        location_type.on.change();
      },
    };

    /**
     * 地域类型
     */
    location_type.on = {
      change() {
        const is_location = geo_type.value === 'LOCATION';
        const is_residence = location_type.value === 'RESIDENCE';
        const is_current = location_type.value === 'CURRENT';
        residence_geo_locations.hidden = !(is_location && is_residence);
        current_geo_locations.hidden = !(is_location && is_current);
      },
    };

    /**
     * 人群类型
     */
    audience_types.on = {
      change() {
        audience_types.value = checkboxFilterNone(audience_types.value, 'INFINITE');
        const is_include_audience = audience_types.value.includes('INCLUDE');
        const is_exclude_audience = audience_types.value.includes('EXCLUDE');
        include_audiences.hidden = !is_include_audience;
        exclude_audiences.hidden = !is_exclude_audience;
      },
    };

    /**
     * 包含人群
     */
    include_audiences.on = {
      change() {
        // 全选
        include_audiences.value = include_audiences.value.filter((item) => !exclude_audiences.value.includes(item));
        // 多选
        exclude_audiences.options.forEach(item => (item.disabled = include_audiences.value.includes(item.value)));
      },
    };

    /**
     * 排除人群
     */
    exclude_audiences.on = {
      change() {
        // 全选
        exclude_audiences.value = exclude_audiences.value.filter((item) => !include_audiences.value.includes(item));
        // 多选
        include_audiences.options.forEach(item => (item.disabled = exclude_audiences.value.includes(item.value)));
      },
    };
  }

  /**
   * 获取差异化字段
   * 该方法为抽象方法，必须由子类实现
   */
  getDifferentiatedOverrides() {
    throw new Error('Must be implemented by subclasses.');
  }

  /**
   * 公共的覆盖
   */
  getPublicOverrides() {
    // 广告名称
    const campaign_name = generateFieldName([CAMPAIGN_MODULE_KEY, CAMPAIGN_BASE_FORM_KEY, 'campaign_name']);

    // 素材标签
    const labels = generateFieldName([CAMPAIGN_MODULE_KEY, CAMPAIGN_BASE_FORM_KEY, 'labels']);

    // 渠道号
    const fusion__channel_code = generateFieldName([CAMPAIGN_MODULE_KEY, CAMPAIGN_DELIVERY_FORM_KEY, 'fusion__channel_code']);

    // 投放内容
    const delivery_content_id = generateFieldName([CAMPAIGN_MODULE_KEY, CAMPAIGN_DELIVERY_FORM_KEY, 'delivery_content_id']);

    // 广告版位类型
    const site_set_type = generateFieldName([CAMPAIGN_MODULE_KEY, CAMPAIGN_SITE_FORM_KEY, 'site_set_type']);

    // 广告版位
    const site_sets = generateFieldName([CAMPAIGN_MODULE_KEY, CAMPAIGN_SITE_FORM_KEY, 'site_sets']);

    // 投放时间类型
    const schedule_type = generateFieldName([CAMPAIGN_MODULE_KEY, CAMPAIGN_SCHEDULE_FORM_KEY, 'schedule_type']);

    // 投放日期
    const delivery_date = generateFieldName([CAMPAIGN_MODULE_KEY, CAMPAIGN_SCHEDULE_FORM_KEY, 'delivery_date']);

    // 投放时间类型
    const time_period_type = generateFieldName([CAMPAIGN_MODULE_KEY, CAMPAIGN_SCHEDULE_FORM_KEY, 'time_period_type']);

    // 投放时间段
    const schedule_time = generateFieldName([CAMPAIGN_MODULE_KEY, CAMPAIGN_SCHEDULE_FORM_KEY, 'schedule_time']);

    // 投放多个时间段
    const time_period = generateFieldName([CAMPAIGN_MODULE_KEY, CAMPAIGN_SCHEDULE_FORM_KEY, 'time_period']);

    // 日预算类型
    const daily_budget_type = generateFieldName([CAMPAIGN_MODULE_KEY, CAMPAIGN_BUDGET_AND_BID_FORM_KEY, 'daily_budget_type']);

    // 日预算
    const daily_budget = generateFieldName([CAMPAIGN_MODULE_KEY, CAMPAIGN_BUDGET_AND_BID_FORM_KEY, 'daily_budget']);

    // 转化事件
    const convert_id = generateFieldName([CAMPAIGN_MODULE_KEY, CAMPAIGN_BUDGET_AND_BID_FORM_KEY, 'convert_id']);

    // 广告分配创意数类型
    const fusion__creative_num_type = generateFieldName([CREATIVE_MODULE_KEY, CREATIVE_BASE_FORM_KEY, 'fusion__creative_num_type']);

    // 广告分配创意数
    const fusion__creative_num = generateFieldName([CREATIVE_MODULE_KEY, CREATIVE_BASE_FORM_KEY, 'fusion__creative_num']);

    // 创意名称
    const creative_name = generateFieldName([CREATIVE_MODULE_KEY, CREATIVE_BASE_FORM_KEY, 'creative_name']);

    // 分配规则
    const dispatch_rule = generateFieldName([CREATIVE_MODULE_KEY, CREATIVE_CONTENT_FORM_KEY, 'dispatch_rule']);

    return {
      [campaign_name]({ field_config }) {
        field_config.isPreview = true;
      },
      [labels]: ({ field_config }) => {
        field_config.isPreview = true;
        field_config.formItemTips.content = '';
      },
      [fusion__channel_code]({ field_config }) {
        const channel_codes = clone(field_config.children.fusion__channel_codes);
        field_config.type = 'label';
        field_config.labelTips = '';
        const content = channel_codes.value.map(advertiser => {
          const alias = advertiser.list.map(({ alias }) => alias).join('、');
          return `${advertiser.label}：${alias}`;
        });
        field_config.value = generateHtmlContent({ content, el: 'div', is_index: false });
        delete field_config.children;
      },
      [delivery_content_id]: ({ field_config }) => {
        field_config.isPreview = true;
      },
      [site_set_type]({ field_config, raw_config, key }) {
        field_config.hidden = (raw_config[key].value === 2);
      },
      [site_sets]({ field_config, raw_config }) {
        field_config.label = '广告版位';
        field_config.labelTips = '';
        field_config.hidden = raw_config['site_set_type'].value === 1;
      },
      [schedule_type]({ field_config, raw_config, key }) {
        field_config.hidden = !(raw_config[key].value === 'SCHEDULE_FROM_NOW');
      },
      [delivery_date]({ field_config }) {
        field_config.label = '投放日期';
      },
      [time_period_type]: ({ field_config, raw_config, key }) => {
        field_config.hidden = !(raw_config[key].value === 'TIME_PERIOD_ALL');
      },
      [schedule_time]: ({ field_config }) => {
        field_config.label = '投放时间';
        field_config.value = `(星期一至星期日) ${field_config.value}`;
      },
      [time_period]: ({ field_config }) => {
        field_config.label = '投放时间';
        field_config.isPreview = true;
      },
      [daily_budget_type]({ field_config, key, raw_config }) {
        field_config.hidden = !(raw_config[key].value === 'BUDGET_MODE_INFINITE');
      },
      [daily_budget]({ field_config }) {
        field_config.label = '日预算';
      },
      [convert_id]({ field_config }) {
        field_config.isPreview = true;
      },
      [fusion__creative_num_type]({ field_config }){
        field_config.hidden = true;
      },
      [fusion__creative_num]({ field_config }){
        field_config.label = '广告分配创意数';
      },
      [creative_name]({ field_config }) {
        field_config.isPreview = true;
      },
      [dispatch_rule]: ({ field_config }) => {
        field_config.labelTips = '';
      },
    };
  }

  /**
   * 合并公共和差异覆盖方法
   * @returns
   */
  getOverrides() {
    return deepMerge([this.getPublicOverrides(), this.getDifferentiatedOverrides()]);
  }

  /**
   * 模块字段排序
   * 该方法为抽象方法，必须由子类实现
   */
  async setFieldsSort() {
    throw new Error('Must be implemented by subclasses.');
  }

  /**
   * 设置动态渲染组件
   */
  setDynamicComponent() {
    return Promise.resolve();
  }

  /**
   * 转换差异化广告预览参数
   * 该方法为抽象方法，必须由子类实现
   */
  transformDifferentiatedCampaignPreviewPayload() {
    throw new Error('Must be implemented by subclasses.');
  }

  /**
   * 转换差异化创意预览参数
   * 该方法为抽象方法，必须由子类实现
   */
  transformDifferentiatedCreativePreviewPayload() {
    throw new Error('Must be implemented by subclasses.');
  }

  /**
   * 获取公共创建定向包接口参数
   * @param {Object} target_data 定向数据
   * @returns {Object}
   */
  getPublicCreateTargetParams(target_data) {
    const { name, geo_location, gender, audience } = target_data;

    return {
      name,
      geo_location,
      ...(gender !== OPTIONS_TYPE.UNLIMITED && { gender }),
      audience,
    };
  }

  /**
   * 获取创建定向包差异化接口参数
   * 该方法为抽象方法，必须由子类实现
   */
  getDifferentiatedCreateTargetParams() {
    throw new Error('Must be implemented by subclasses.');
  }

  /**
   * 获取创建定向包接口参数
   * @param {Object} target_data 定向数据
   * @param {Object} target_form_config 定向表单配置
   * @returns {Object}
   */
  getCreateTargetParams(target_data, target_form_config) {
    return Object.assign(
      this.getPublicCreateTargetParams(target_data),
      this.getDifferentiatedCreateTargetParams(target_data, target_form_config)
    );
  }

  /**
   * 获取公共定向包详情表单数据
   * @param {Object} target_params 定向详情数据
   * @returns {Object}
   */
  getPublicTargetDetailFormData(target_params) {
    const { name, geo_location, gender, audience } = target_params;

    return {
      name,
      geo_location,
      ...(gender && { gender }),
      audience,
    };
  }

  /**
   * 获取定向包差异化详情表单数据
   * 该方法为抽象方法，必须由子类实现
   */
  getDifferentiatedTargetDetailFormData() {
    throw new Error('Must be implemented by subclasses.');
  }

  /**
   * 获取定向包详情表单数据
   * @param {Object} target_params 定向详情数据
   * @returns {Object}
   */
  getTargetDetailFormData(target_params) {
    return Object.assign(
      this.getPublicTargetDetailFormData(target_params),
      this.getDifferentiatedTargetDetailFormData(target_params),
    );
  }

  /**
   * 获取定向数据转预览数据关联表
   * @returns {Object}
   */
  getTargetPreviewAssociatedMap() {
    return {};
  }

  /**
   * 转换提交预览接口所需结构
   * @param {Object} data 数据
   * @param {Object} data.form_assign_data 提交参数
   * @param {Object} data.init_ops 初始化数据
   * @param {Object} data.extra_config 配置数据
   * @returns {Object}
   */
  transformPreviewPayload({ form_assign_data, init_ops, extra_config }) {
    const { channel_interface, advertiser_ids } = init_ops;
    const { is_add_creative, add_creative_list = [] } = extra_config;
    const {
      campaign_config: {
        base_form: {
          campaign_name,
          is_label,
          labels = {},
        } = {},
        delivery_form: {
          fusion__channel_code: {
            fusion__channel_codes,
          } = {},
          delivery_platform_type,
          delivery_content_id,
        } = {},
        site_form: {
          site_set_type,
          site_sets,
        } = {},
        schedule_form: {
          schedule_type,
          delivery_date,
        } = {},
        budget_and_bid_form: {
          daily_budget_type,
          daily_budget,
          price_type,
          price,
          price_strategy,
          convert_id,
          deep_convert_type,
          deep_convert_price,
        } = {},
      } = {},
      creative_config: {
        base_form: {
          creative_name,
          fusion__creative_num_type,
          fusion__creative_num,
        },
        content_form: {
          dispatch_rule,
        },
      },
      material_config,
      title_config,
      targeting_config,
      site_config,
    } = form_assign_data;

    // 素材标签
    const labels_value = Object.entries(labels).map(([advertiser_id, list]) => {
      return {
        advertiser_id,
        labels: list.map(({ label_id, channel_label_id: channel_id }) => ({ label_id, channel_id })),
      };
    });
    const images = (material_config?.images?.length ? material_config.images : []).map(({ id, url, name }) => ({ id, urls: url.map(u => u.url), name }));
    const videos = (material_config?.videos?.length ? material_config.videos : []).map(({ id, url, name, width, height }) => ({ id, url: url[0].url, cover: url[0].thumb_url, name, width, height, ...(url[0].horizontal_cover && { horizontal_cover: url[0].horizontal_cover }) }));
    const titles = (title_config?.length ? title_config : []).map(({ name }) => name);
    const targetings = (targeting_config?.length ? targeting_config : []).reduce((acc, cur) => {
      const item = acc.find(i => i.advertiser_id === cur.advertiser_id);
      item ? item.targetings.push({ id: cur.id, name: cur.name }) : acc.push({ advertiser_id: cur.advertiser_id, targetings: [{ id: cur.id, name: cur.name }] });
      return acc;
    }, []);
    const sites = (site_config?.length ? site_config : []).reduce((acc, cur) => {
      const item = acc.find(i => i.advertiser_id === cur.advertiser_id);
      const sites_item = {
        id: cur.id,
        name: cur.name,
        ...(cur.site_type && { site_type: cur.site_type }),
        ...(cur.url && { url: cur.url }),
      };
      item ? item.sites.push(sites_item) : acc.push({ advertiser_id: cur.advertiser_id, sites: [sites_item] });
      return acc;
    }, []);
    const convert_data = convert_id ? JSON.parse(convert_id) : null;
    const channels = (fusion__channel_codes?.length ? fusion__channel_codes : []).map(({ advertiser_id, list }) => {
      return {
        advertiser_id,
        channel_aliases: list.map(({ alias }) => alias),
      };
    });
    const converts = convert_data ? (fusion__channel_codes?.length ? fusion__channel_codes : []).map(({ advertiser_id, list }) => {
      return {
        advertiser_id,
        converts: list.map(({ alias, conversion }) => {
          const conversion_data = channel_interface === CHANNEL_INTERFACE_TYPE.HUAWEI ? convert_data : JSON.parse(conversion);
          const { id, name, convert_goal, deep_convert_goal } = conversion_data;
          return {
            id,
            name,
            channel_alias: alias,
            ...(convert_goal && { convert_goal }),
            // 华为的深度优化目标 - 取深度优化目标表单中的值
            ...((channel_interface === CHANNEL_INTERFACE_TYPE.HUAWEI && deep_convert_type) && { deep_convert_type }),
            // 广点通的深度优化目标 - 取转化中的深度优化目标值
            ...((channel_interface === CHANNEL_INTERFACE_TYPE.GDT && deep_convert_goal) && { deep_convert_type: deep_convert_goal }),
            // 广点通的深度优化ROI系数
            ...(conversion_data.gdt_params && { is_roi: conversion_data.gdt_params.is_roi }),
          };
        }),
      };
    }) : null;
    const strategy = {
      oneForOne: () => {
        const result_map = delivery_content_id?.result_map || {};
        return {
          delivery_contents: Object.entries(result_map).map(([advertiser_id, { value, options }]) => {
            const strategy = {
              [CHANNEL_INTERFACE_TYPE.JRTT]: () => {
                const delivery_content = options.find(opt => value === opt.value);
                return { package_name: delivery_content?.jrtt_params?.package_name };
              },
              [CHANNEL_INTERFACE_TYPE.JRTT_V3]: () => {
                const delivery_content = options.find(opt => value === opt.value);
                return { package_name: delivery_content?.jrtt_v3_params?.package_name };
              },
              default: () => {},
            };
            return {
              advertiser_id,
              id: value?.split('#')?.[0],
              name: value?.split('#')?.[1],
              ...((strategy[channel_interface] || strategy['default'])()),
            };
          }),
        };
      },
      oneForMore: () => {
        return {
          delivery_contents: advertiser_ids.map(advertiser_id => {
            return {
              advertiser_id,
              id: delivery_content_id?.split('#')?.[0],
              name: delivery_content_id?.split('#')?.[1],
            };
          }),
        };
      },
    };
    const payload = {
      channel_interface,
      advertiser_ids,
      is_created_by_existing_campaign: is_add_creative ? 1 : 0,
      ...((is_add_creative && add_creative_list.length) && {
        campaign_ids: [{
          advertiser_id: add_creative_list[0].advertiser_id,
          ids: add_creative_list.map(({ id }) => id),
        }],
      }),
      ...(!is_add_creative && {
        campaign: {
          /** 渠道号信息 */
          channels,
          /** 广告前缀名称 */
          campaign_pre_name: campaign_name,
          /** 是否使用素材标签 */
          ...(is_label && { is_label }),
          /** 素材标签 */
          ...(is_label && labels_value.length && { labels: labels_value }),
          /** 投放平台 */
          delivery_platform_type,
          /**
           * 投放内容ID
           * 投放内容名称
           */
          ...([CHANNEL_INTERFACE_TYPE.GDT].includes(channel_interface) ? strategy.oneForMore() : strategy.oneForOne()),
          /** 广告版位类型 */
          site_set_type,
          /** 版位ID */
          ...(site_set_type === 2 && { site_sets: site_sets.includes('SITE_SET_WECHAT|SITE_SET_WECHAT_PLUGIN') ? [...site_sets.filter(v => v !== 'SITE_SET_WECHAT|SITE_SET_WECHAT_PLUGIN'), 'SITE_SET_WECHAT', 'SITE_SET_WECHAT_PLUGIN'] : site_sets }),
          /** 定向包 */
          ...(targetings?.length && { targetings }),
          /** 投放日期类型 */
          schedule_type,
          /** 开始投放日期 */
          ...(schedule_type === 'SCHEDULE_START_END' && { begin_date: delivery_date[0] }),
          /** 结束投放日期 */
          ...(schedule_type === 'SCHEDULE_START_END' && { end_date: delivery_date[1] }),
          /** 日预算类型 */
          ...(daily_budget_type && { daily_budget_type }),
          /** 日预算 */
          ...(daily_budget_type === 'BUDGET_MODE_DAY' && { daily_budget: calculator.times(daily_budget, 100) }),
          /** 出价方式 */
          ...(price_type && { price_type }),
          /** 出价 */
          ...(price && { price: calculator.times(price, 100) }),
          /** 出价策略 */
          ...(price_strategy && { price_strategy }),
          /** 转化 */
          ...(converts && { converts }),
          /** 深度优化出价 */
          ...(deep_convert_price && { deep_convert_price: calculator.times(deep_convert_price, 100) }),
        },
      }),
      creative: {
        /** 创意名称 */
        creative_name,
        /** 每个广告下创建的创意数量 */
        ...(fusion__creative_num_type === 'specify_num' && { creative_num_per_campaign: fusion__creative_num }),
        /** 图片素材 */
        ...(images?.length && { images }),
        /** 视频素材 */
        ...(videos?.length && { videos }),
        /** 标题 */
        ...(titles?.length && { titles }),
        /** 落地页 */
        ...(sites?.length && { sites }),
        /** 分配规则 */
        dispatch_rule,
      },
      [`${channel_interface}_params`]: {
        ...(!is_add_creative && {
          campaign: this.transformDifferentiatedCampaignPreviewPayload({ form_assign_data }),
        }),
        creative: this.transformDifferentiatedCreativePreviewPayload({ form_assign_data, extra_config, init_ops }),
      },
    };

    return payload;
  }

  /**
   * 转换提交计划接口所需结构
   * @param {Object} data
   * @param {Object} data.advertiser_sources 广告源数据
   * @param {string} data.channel_interface 渠道接口
   * @returns {Object}
   */
  transformSubmitPayload({ advertiser_sources, channel_interface }) {
    const batch_params = [];
    Object.entries(advertiser_sources).forEach(([advertiser_id, { campaigns }]) => {
      const payload = {
        advertiser_id,
        plans: campaigns.map(({ creatives, ...campaign }) => {
          return {
            campaign,
            creatives,
          };
        }),
      };
      batch_params.push(payload);
    });
    return {
      channel_interface,
      batch_params,
    };
  }

  /**
   * 添加字段限制
   * @param {Object} fields 表单字段配置
   * @param {Object} config 配置信息
   */
  addFieldsRestriction(fields, config) {
    const {
      campaign_config: {
        config: {
          base_form,
          delivery_form: {
            config: {
              fusion__channel_code,
            },
          },
        },
      },
      creative_config: {
        config: {
          base_form: {
            config: { fusion__creative_num },
          },
        },
      },
    } = fields;
    const { campaign_name } = base_form.config;
    const { channel_config } = config;
    const {
      campaign: campaign_restriction,
      creative: creative_restriction,
    } = channel_config;

    const { campaign_name_separator, max_campaign_num } = campaign_restriction;
    const { max_creative_num } = creative_restriction;

    // 计划名称规则
    campaign_name.rules[campaign_name.rules.length] = createCampaignNameSpecialRule(campaign_name_separator);

    // 一次最多可提交的创意数量
    fusion__creative_num.props.max = max_creative_num;

    const { fusion__channel_code_count } = fusion__channel_code.children;

    // 一次最多可提交的广告数量
    fusion__channel_code_count.props.max = max_campaign_num;
  }

  /**
   * 是否显示预览按钮
   * @param {Object} form_assign_data 分配数据
   * @param {Object} form_fields 源数据
   * @returns {boolean}
   */
  isShowPreviewBtn(form_assign_data, form_fields) {
    const is_equal = Object.keys(form_assign_data).length === Object.keys(form_fields).length;
    if (!is_equal) {
      return false;
    }
    const keys = [TARGETING_MODULE_KEY, TITLE_MODULE_KEY, SITE_MODULE_KEY];
    const material = form_assign_data[MATERIAL_MODULE_KEY];
    return keys.every(key => form_assign_data[key].length > 0) && (material.images.length > 0 || material.videos.length > 0);
  }

  /**
   * 获取素材属性
   * @returns {Object}
   */
  getMaterialProps() {
    return {};
  }

  /**
   * 设置源数据表单
   * @returns {void}
   */
  setSourceFormFieldsData() {
    return;
  }

  /**
   * 设置源数据表单 - 广告
   * setSourceFormFieldsData和setSourceFormFieldsDataOfCampaign
   * 这里后续应合并为同一个方法，几乎影响全渠道，影响较大延后优化
   * @param {Object} source 源数据
   * @param {Object} target 目标数据
   * @param {Object} form_assign_data 分配数据
   * @returns {void}
   */
  setSourceFormFieldsDataOfCampaign(source, target, form_assign_data) {
    const {
      campaign_config: {
        config: {
          base_form: {
            config: {
              is_label,
            },
          },
        },
      },
      material_config: {
        config: {
          material_form: {
            config: {
              material,
            },
          },
        },
      },
    } = source;

    const map_tuple = [
      /**
       * 素材标签为【使用(1)】时
       * - 清空素材数据
       */
      [
        () => is_label.value === 1,
        () => {
          material.value.images?.splice?.(0);
          material.value.videos?.splice?.(0);
          delete form_assign_data.material_config;
        },
      ],
    ];

    map_tuple.forEach(([condition, handler]) => (condition() && handler()));
  }

  /**
   * 是否重新渲染创意
   * @returns {boolean}
   */
  isReRenderCreative() {
    return false;
  }

  /**
   * 获取公共模板字段
   * 空数组表示存储整个模块
   * @returns {Object}
   */
  getPublicTemplateMap() {
    return {
      campaign_config: {
        delivery_form: ['delivery_platform_type'],
        site_form: [],
        schedule_form: [],
        budget_and_bid_form: ['daily_budget_type', 'daily_budget', 'price_type', 'price_strategy', 'price'],
      },
      creative_config: {
        base_form: ['fusion__creative_num_type', 'fusion__creative_num'],
        content_form: ['dispatch_rule'],
        creative_components: [],
      },
    };
  }

  /**
   * 获取差异化模板字段
   * 该方法为抽象方法，必须由子类实现
   */
  getDifferentiatedTemplateMap() {
    throw new Error('Must be implemented by subclasses.');
  }

  /**
   * 合并公共模板字段和差异化模板字段
   * @returns {Object}
   */
  getTemplateMap() {
    return deepMerge([this.getPublicTemplateMap(), this.getDifferentiatedTemplateMap()]);
  }

  /**
   * 初始化渠道接口配置
   * @returns {Promise}
   */
  initChannelInterfaceConfig() {
    return Promise.resolve();
  }
}
